'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import { Mail, Check, Sparkles } from 'lucide-react';

export default function WhereWereHeaded() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    // BACKEND: POST /api/waitlist/photo-frame — save email, send Resend admin notification to admin@introspectlabs.com
    await new Promise((r) => setTimeout(r, 800));
    setSubmitted(true);
    setLoading(false);
    toast.success('You\'re on the AI Photo Frame waitlist! 🖼️');
  };

  return (
    <section className="py-24 relative">
      <div className="absolute inset-0 gradient-bg-subtle pointer-events-none" />
      <div className="relative max-w-screen-xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-12">
          <span className="text-xs font-semibold tracking-widest text-primary uppercase mb-4 block">
            Where we're headed
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            The{' '}
            <span className="gradient-text">AI Photo Frame</span>
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto px-4">
            A compassionate AI memory frame that understands your emotional rhythms and transforms your space with visuals that comfort, inspire, and reflect your journey.
          </p>
        </div>

        {/* Video */}
        <div className="max-w-3xl mx-auto mb-12 rounded-3xl overflow-hidden glass border border-[var(--glass-border)] aspect-video relative">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            poster="https://ik.imagekit.io/uyir/AZ2fX79So7BUMJLvdA_Q-g-AZ2fX79Svv9lox2dyrmZCQ.mp4?tr=f-auto"
          >
            <source
              src="https://ik.imagekit.io/uyir/AZ2fX79So7BUMJLvdA_Q-g-AZ2fX79Svv9lox2dyrmZCQ.mp4?tr=f-auto"
              type="video/mp4"
            />
            <div className="absolute inset-0 gradient-bg flex items-center justify-center">
              <p className="text-white/60 text-sm">Video preview coming soon</p>
            </div>
          </video>
        </div>

        {/* Waitlist */}
        <div className="max-w-lg mx-auto glass rounded-3xl p-8 border border-[var(--glass-border)] text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles size={20} className="text-primary" />
            <p className="text-sm font-semibold text-foreground">
              Interested in the AI Photo Frame?
            </p>
          </div>
          <p className="text-xs text-muted-foreground mb-6">
            Join the waitlist and we'll reach out when it's ready for early buyers.
          </p>

          {submitted ? (
            <div className="flex items-center justify-center gap-2 py-4 text-green-400">
              <Check size={20} />
              <span className="font-medium">You're on the waitlist!</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full pl-9 pr-4 py-3 rounded-xl bg-input border border-[var(--glass-border)] text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 transition-colors"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto px-5 py-3 rounded-xl gradient-bg text-white text-sm font-semibold disabled:opacity-60 hover:opacity-90 active:scale-95 transition-all duration-150"
              >
                {loading ? (
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  'Join waitlist'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}